import { IDocumentAssemblerGenerationPlaceholder } from '@commenda/api';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  EmptyState,
  Icons,
  useToggle,
} from '@commenda/kakashi';

import type { DocumentAssemblerSurroundingContextGroup } from './document-assembler.types';
import { DocumentAssemblerGenerationPlaceholderChip } from './DocumentAssemblerGenerationPlaceholderChip';
import { DocumentAssemblerGenerationSurroundingContextHelper } from './DocumentAssemblerGenerationSurroundingContextHelper';

interface DocumentAssemblerGenerationDocumentViewProps {
  placeholders: IDocumentAssemblerGenerationPlaceholder[];
}

const Paragraph = ({
  group,
}: {
  group: DocumentAssemblerSurroundingContextGroup;
}) => (
  <p className="txt-small-regular whitespace-pre-wrap text-text-secondary">
    {DocumentAssemblerGenerationSurroundingContextHelper.toSegments(group).map(
      (segment) =>
        segment.kind === 'PROSE' ? (
          <span key={segment.key}>{segment.text}</span>
        ) : (
          <DocumentAssemblerGenerationPlaceholderChip
            key={segment.key}
            placeholder={segment.placeholder}
          />
        )
    )}
  </p>
);

const LeftOutRun = ({
  groups,
}: {
  groups: DocumentAssemblerSurroundingContextGroup[];
}) => {
  const leftOutRunVisibility = useToggle(false);
  const slotCount = groups.reduce(
    (total, group) => total + group.placeholders.length,
    0
  );

  return (
    <Collapsible
      open={leftOutRunVisibility.value}
      onOpenChange={leftOutRunVisibility.toggle}
    >
      <div className="flex items-center justify-between gap-lg rounded-lg border border-border-secondary bg-bg-secondary px-lg py-md">
        <CollapsibleTrigger className="flex items-center gap-sm text-left">
          {leftOutRunVisibility.value ? (
            <Icons.ChevronUp className="size-4 text-text-tertiary" />
          ) : (
            <Icons.ChevronDown className="size-4 text-text-tertiary" />
          )}
          <span className="txt-small-medium text-text-secondary">
            {groups.length === 1 ? '1 passage' : `${groups.length} passages`}{' '}
            left out of the document, holding{' '}
            {slotCount === 1 ? '1 slot' : `${slotCount} slots`} — the branch
            they sit in was not chosen
          </span>
        </CollapsibleTrigger>
      </div>
      <CollapsibleContent className="flex flex-col gap-lg px-lg pt-lg">
        {groups.map((group) => (
          <Paragraph key={group.paragraphKey} group={group} />
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

const toConsecutiveRuns = (
  groups: DocumentAssemblerSurroundingContextGroup[]
): DocumentAssemblerSurroundingContextGroup[][] => {
  const runs: DocumentAssemblerSurroundingContextGroup[][] = [];

  for (const group of groups) {
    const previousRun = runs[runs.length - 1];

    if (
      group.isEveryPlaceholderLeftOut &&
      previousRun?.[0]?.isEveryPlaceholderLeftOut
    ) {
      previousRun.push(group);
    } else {
      runs.push([group]);
    }
  }

  return runs;
};

export const DocumentAssemblerGenerationDocumentView = ({
  placeholders,
}: DocumentAssemblerGenerationDocumentViewProps) => {
  const groups =
    DocumentAssemblerGenerationSurroundingContextHelper.groupByParagraph(
      placeholders
    );

  if (!groups.length) {
    return (
      <EmptyState
        heading="This template asked for no values"
        description="Its version declares no placeholders."
      />
    );
  }

  return (
    <div className="flex flex-col gap-xl rounded-xl border border-border-primary bg-bg-primary p-xl">
      {toConsecutiveRuns(groups).map((run, index) => (
        <div key={run[0].paragraphKey} className="flex flex-col gap-xl">
          {index > 0 ? (
            <span className="txt-extra-small-regular text-center text-text-quaternary">
              · · ·
            </span>
          ) : null}
          {run[0].isEveryPlaceholderLeftOut ? (
            <LeftOutRun groups={run} />
          ) : (
            <Paragraph group={run[0]} />
          )}
        </div>
      ))}
    </div>
  );
};
